import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contacts, inbox } from '@repletoryreach/sdk-api';
import { useDebounce, useMediaQuery } from '@repletoryreach/sdk-utils/hooks';
import { Stack, Card, Group, Button, Text, Badge } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, DataTable, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Menu, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../app/guides/HelpButton';
import { useT } from '../../i18n/I18nProvider';
import { formatDate, formatCount, type Indexed } from '../../lib/format';
import { usePersistedState } from '../../lib/usePersistedState';
import { useSmsStatus } from '../../lib/sms';
import { useLanguages } from '../../lib/languages';
import { openImportPreview } from './components/ImportPreviewModal';
import { openCsvFormatModal } from './components/CsvFormatModal';
import { openEraseModal } from './components/EraseModal';
import { ContactCards } from './components/ContactCards';
import { ContactDrawer, type DrawerTarget } from './components/ContactDrawer';
import { ContactIdentity, contactKeyOf, contactLabelOf } from './components/ContactAvatar';
import { ContactTags, OptInPill } from './components/ContactTag';
import { useTagVocabulary } from './components/TagPicker';
import { useContactFields } from './components/tabs/FieldsTab';
import { BulkBar } from './components/BulkBar';
import './contacts.css';

/** Page sizes the footer offers, matching the reference. */
const PAGE_SIZES = [25, 50, 100];

/** Opt-in filter, as a string so it round-trips through a `Select`. */
type OptInFilter = '' | 'in' | 'out';

/** The three columns the service will sort by. */
type SortColumn = 'name' | 'opt_in' | 'updated_at';

/** Compose's own cap, read from the SDK so the two cannot disagree. */
const MAX_COMPOSE_RECIPIENTS = inbox.COMPOSE_MAX_RECIPIENTS;

export default function ContactsPage() {
  const t = useT();
  // SMS consent and its import column exist only while the platform offers SMS.
  const { available: smsAvailable } = useSmsStatus();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const narrow = useMediaQuery('(max-width: 820px)'); // i18n-ignore: CSS media query

  const [query, setQuery] = useState('');
  const search = useDebounce(query, 300);
  const [tag, setTag] = usePersistedState('tenant.contacts.tag', '');
  const [optInFilter, setOptInFilter] = usePersistedState<OptInFilter>('tenant.contacts.optIn', '');
  /*
   * The contact's language (1 November 2026), as a catalogue code. The service matches the base code of each
   * contact's locale, so "en" also finds "en-ZA" and a locale typed as "English". The options are the platform's
   * languages, each by its own name.
   */
  const [language, setLanguage] = usePersistedState('tenant.contacts.language', '');
  const { languages } = useLanguages();
  /*
   * One custom-field filter: which field, and a value suited to its type.
   * Sent as `f.<key>`; an empty value means "has any value". The key is only
   * used once the definitions have loaded and still list it: a field deleted
   * in Settings would otherwise be refused by the service on every load.
   */
  const [fieldKey, setFieldKey] = usePersistedState('tenant.contacts.fieldKey', '');
  const [fieldValueRaw, setFieldValueRaw] = usePersistedState('tenant.contacts.fieldValue', '');
  const fieldValue = useDebounce(fieldValueRaw, 300);
  const { defs: fieldDefs, loading: fieldsLoading } = useContactFields();
  const activeFieldDefs = fieldDefs.filter((d) => d.is_active);
  const fieldDef = activeFieldDefs.find((d) => d.key === fieldKey);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePersistedState('tenant.contacts.pageSize', 25);
  const [editing, setEditing] = useState<DrawerTarget>(null);

  /**
   * Message these people: hand them to compose, already chosen.
   *
   * Compose reads `?to=` on arrival, so one contact and fifty arrive the same
   * way and the person lands on a screen that already knows who it is for.
   */
  function messageContacts(msisdns: string[]) {
    const to = msisdns.filter(Boolean).slice(0, MAX_COMPOSE_RECIPIENTS);
    if (to.length === 0) return;
    navigate(`/inbox/compose?to=${encodeURIComponent(to.join(','))}`);
  }

  function openContact(target: DrawerTarget) {
    setEditing(target);
    setParams((prev) => {
      const q = new URLSearchParams(prev);
      if (target && target !== 'new') q.set('contact', target.id);
      else q.delete('contact');
      return q;
    }, { replace: true });
  }

  function closeContact() {
    setEditing(null);
    setParams((prev) => {
      const q = new URLSearchParams(prev);
      q.delete('contact');
      return q;
    }, { replace: true });
  }
  const [exporting, setExporting] = useState(false);
  // Which rows the bulk bar acts on. Ids, not rows: a selection has to survive
  // a refetch, and the row object does not.
  const [selected, setSelected] = useState<string[]>([]);
  const [importHelpOpen, setImportHelpOpen] = usePersistedState('tenant.contacts.importHelp', true);
  // Sorted server-side. Sorting a page in the browser sorts fifty rows out of
  // a book of four thousand and calls it "sorted by name".
  const [sort, setSort] = usePersistedState<SortColumn | ''>('tenant.contacts.sort', '');
  const [order, setOrder] = usePersistedState<'asc' | 'desc'>('tenant.contacts.order', 'desc');
  const fileRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  /*
   * `?contact=<id>` opens the drawer. That is what `/contacts/:id` redirects
   * to, so every link that ever pointed at the old page still lands on the
   * right person, and the URL of an open contact is still shareable.
   */
  const [params, setParams] = useSearchParams();
  const openContactId = params.get('contact');

  const filters = useMemo(() => ({
    ...(search.trim() ? { search: search.trim() } : {}),
    ...(tag ? { tag } : {}),
    ...(optInFilter ? { opt_in: optInFilter === 'in' } : {}),
    ...(language ? { language } : {}),
    ...(fieldDef ? { fields: { [fieldDef.key]: fieldValue.trim() } } : {})
  }), [search, tag, optInFilter, language, fieldDef, fieldValue]);

  // A field that no longer exists is not a filter, it is a stale preference.
  useEffect(() => {
    if (!fieldsLoading && fieldKey && !fieldDef) { setFieldKey(''); setFieldValueRaw(''); }
  }, [fieldsLoading, fieldKey, fieldDef]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * §4 item 1: this fetched a flat 200 rows and filtered them in the browser,
   * so an org with 3 000 contacts saw 200, the caption read "200 shown", and
   * searching for a contact who existed past row 200 returned nothing, which
   * invites the user to create a duplicate. `search`, `tag` and `opt_in` have
   * all been bound server-side since W46 (§4 item 8) and none was used.
   */
  const { data, isPending, error, isFetching } = useQuery({
    queryKey: ['contacts', 'list', filters, page, pageSize, sort, order],
    queryFn: () => contacts.listContacts({
      ...filters,
      ...(sort ? { sort, order } : {}),
      limit: pageSize,
      offset: (page - 1) * pageSize
    }),
    placeholderData: (prev) => prev
  });

  // A changed filter starts at page one, otherwise page 4 of the old result
  // renders as an empty page of the new one.
  useEffect(() => { setPage(1); }, [search, tag, optInFilter, language, fieldKey, fieldValue, pageSize]);
  // A selection that survives a filter change is a selection of rows the user
  // can no longer see, and the bulk bar would still act on them.
  useEffect(() => { setSelected([]); }, [search, tag, optInFilter, language, fieldKey, fieldValue, page]);

  // A deep link carries only an id; the drawer fetches the rest.
  useEffect(() => {
    if (!openContactId) return;
    setEditing((prev) => (prev && prev !== 'new' && prev.id === openContactId ? prev : { id: openContactId }));
  }, [openContactId]);

  const rows = (data?.data ?? []) as Indexed<contacts.Contact>[];
  const total = data?.meta?.total ?? 0;
  const filtered = !!search.trim() || !!tag || !!optInFilter || !!language || !!fieldDef;

  function clearFilters() {
    setQuery(''); setTag(''); setOptInFilter(''); setLanguage(''); setFieldKey(''); setFieldValueRaw('');
  }

  /** The language filter's options: each language by its own name, and a chosen one the list no longer has kept. */
  const languageChoices = useMemo(() => {
    const options = languages.map((l) => ({ value: l.code, label: l.native_name }));
    if (language && !languages.some((l) => l.code === language)) options.push({ value: language, label: language });
    return options;
  }, [languages, language]);
  const languageName = languageChoices.find((o) => o.value === language)?.label ?? language;

  /*
   * The tag vocabulary comes from the server.
   *
   * It used to be built from whatever rows happened to be on screen, so a tag
   * used only by contacts on page four was not offered in the filter at all,
   * and there was no way to know how many people carried one. Tags are the
   * audience for a broadcast, so both of those mattered.
   */
  const { tags: vocabulary } = useTagVocabulary();

  // Remove, not delete (2 October 2026): the number stays opted out so no
  // broadcast reaches the person by accident. Erasing is in the contact's
  // own panel and behind "Erase a number".
  const del = useMutation({
    mutationFn: (c: contacts.Contact) => contacts.deleteContact(c.id),
    // §4 item 2: a delete that 404'd looked exactly like one that worked.
    onSuccess: (_r, c) => {
      notifySuccess(t('contacts.remove.toast', { msisdn: contactKeyOf(c) }), t('contacts.remove.toastTitle'));
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (e) => notifyError(
      e instanceof Error ? e.message : t('common.unknownError'),
      t('contacts.remove.failed')
    )
  });

  function confirmDelete(c: contacts.Contact) {
    openConfirmModal({
      title: t('contacts.remove.title', { msisdn: contactKeyOf(c) }),
      children: (
        <Stack gap={8}>
          <Text role="bodySmall">{t('contacts.remove.body')}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{t('contacts.remove.back')}</Text>
        </Stack>
      ),
      labels: { confirm: t('contacts.remove.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => del.mutate(c)
    });
  }

  async function onExport(format: 'csv' | 'xlsx') {
    setExporting(true);
    try {
      const blob = await contacts.exportContacts({ ...filters, format });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      notifySuccess(t('contacts.toast.exported'), t('contacts.title'));
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('contacts.toast.exportFailed'));
    } finally {
      setExporting(false);
    }
  }

  const allOnPageSelected = rows.length > 0 && rows.every((c) => selected.includes(c.id));
  function toggleAll() {
    setSelected(allOnPageSelected ? [] : rows.map((c) => c.id));
  }
  function toggleOne(id: string) {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  /** Clicking a header cycles ascending, descending, then back to the default. */
  function toggleSort(column: SortColumn) {
    if (sort !== column) { setSort(column); setOrder('asc'); return; }
    if (order === 'asc') { setOrder('desc'); return; }
    setSort('');
  }

  /** A header that sorts, with the current direction on it. */
  const sortable = (column: SortColumn, label: string, hint?: string) => (
    <button
      type="button"
      className={`rr-contacts__sort${sort === column ? ' is-active' : ''}`}
      aria-label={t('contacts.sortBy', { column: label })}
      aria-sort={sort === column ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={(e) => { e.stopPropagation(); toggleSort(column); }}
    >
      {label}
      {hint && (
        <Tooltip label={hint} position="top">
          <span className="rr-contacts__hint" aria-hidden><InfoIcon /></span>
        </Tooltip>
      )}
      <SortIcon direction={sort === column ? order : null} />
    </button>
  );

  const activeFilterCount = (search.trim() ? 1 : 0) + (tag ? 1 : 0) + (optInFilter ? 1 : 0) + (language ? 1 : 0) + (fieldDef ? 1 : 0);

  return (
    <Stack gap="lg" className="rr-contacts">
      <PageHeader
        title={t('contacts.title')}
        description={t('contacts.description')}
        withDivider={false}
        actions={
          <Group gap="xs">
            <HelpButton guide="audience" />
            <input
              ref={fileRef}
              type="file"
              // Both formats. The service flattens a workbook to CSV at upload,
              // so there is one importer behind these.
              accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="rr-visually-hidden"
              aria-label={t('contacts.actions.import')}
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                // §4 item 5: the import was fire-and-forget and handed the user
                // a UUID. This drives one toast from queued through to a real
                // row count.
                // The numbers are checked and shown first: what will be
                // assumed, and which rows cannot be read, with the country
                // changeable before anything is written.
                if (f) openImportPreview(f, qc, t('contacts.import.preview.title'));
                e.currentTarget.value = '';
              }}
            />

            {/* Export is two formats now, so it is a menu rather than a button
                that silently picks one. */}
            <Menu>
              <Menu.Target>
                <Button size="sm" variant="default" loading={exporting} leftSection={<DownloadIcon />}>
                  {t('action.export')}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => onExport('csv')}>{t('contacts.export.csv')}</Menu.Item>
                <Menu.Item onClick={() => onExport('xlsx')}>{t('contacts.export.xlsx')}</Menu.Item>
              </Menu.Dropdown>
            </Menu>

            {/* A POPIA request from someone who is not in the list (removed
                earlier, or who only ever wrote in). Rare, so out of the way. */}
            <Button
              size="sm"
              variant="subtle"
              color="gray"
              data-guide="contacts-erase"
              onClick={() => openEraseModal({ title: t('contacts.erase.byNumber.titleEither'), qc })}
            >
              {t('contacts.erase.byNumber.open')}
            </Button>

            <Button
              size="sm"
              variant="default"
              leftSection={<UploadIcon />}
              data-guide="contacts-import"
              // §4 item 6: "Import CSV" opened a file picker with no word about
              // what the file should contain.
              onClick={() => openCsvFormatModal(t, () => fileRef.current?.click(), smsAvailable)}
            >
              {t('contacts.actions.import')}
            </Button>
            <Button size="sm" leftSection={<PlusIcon />} data-guide="contacts-new" onClick={() => setEditing('new')}>
              {t('contacts.actions.create')}
            </Button>
          </Group>
        }
      />

      {error && <Alert color="error" role="alert">{t('contacts.loadError')}</Alert>}

      <Card withBorder noPadding className="rr-contacts__panel" data-guide="contacts-list">
        {/* Filters, labelled. They were a bare row of controls with only
            aria-labels, so on screen nothing said what the two dropdowns
            narrowed. */}
        <div className="rr-contacts__filters" data-guide="contacts-filters">
          <label className="rr-contacts__filter">
            <span className="rr-contacts__filter-label">{t('contacts.search.label')}</span>
            <span className="rr-contacts__search">
              <span className="rr-contacts__search-icon" aria-hidden><SearchIcon /></span>
              <TextInput
                ref={searchRef}
                type="search"
                className="rr-contacts__search-input"
                placeholder={t('contacts.search.placeholder')}
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
              />
            </span>
          </label>

          <label className="rr-contacts__filter">
            <span className="rr-contacts__filter-label">{t('contacts.filters.optIn')}</span>
            <Select
              value={optInFilter}
              onChange={(e) => setOptInFilter(e.currentTarget.value as OptInFilter)}
              data={[
                { value: '', label: t('contacts.filters.optIn.any') },
                { value: 'in', label: t('contacts.optIn.in') },
                { value: 'out', label: t('contacts.optIn.out') }
              ]}
            />
          </label>

          <label className="rr-contacts__filter">
            <span className="rr-contacts__filter-label">{t('contacts.filters.tag')}</span>
            {/* The count beside each tag is the audience size, which is the
                question a tag is usually being looked up to answer. */}
            <Select
              value={tag}
              onChange={(e) => setTag(e.currentTarget.value)}
              data={[
                { value: '', label: t('contacts.filters.tag.any') },
                ...vocabulary.map((x) => ({
                  value: x.tag,
                  label: t('contacts.filters.tag.option', { tag: x.tag, count: x.count })
                }))
              ]}
            />
          </label>

          <label className="rr-contacts__filter">
            <span className="rr-contacts__filter-label">{t('contacts.filters.language')}</span>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.currentTarget.value)}
              data={[
                { value: '', label: t('contacts.filters.language.any') },
                ...languageChoices
              ]}
            />
          </label>

          {/* The custom-field filter: the field, then a value input suited to
              its type. Only shown once the organisation has a field. */}
          {activeFieldDefs.length > 0 && (
            <label className="rr-contacts__filter">
              <span className="rr-contacts__filter-label">{t('contacts.filters.field')}</span>
              <Select
                value={fieldDef ? fieldDef.key : ''}
                onChange={(e) => { setFieldKey(e.currentTarget.value); setFieldValueRaw(''); }}
                data={[
                  { value: '', label: t('contacts.filters.field.any') },
                  ...activeFieldDefs.map((d) => ({ value: d.key, label: d.label }))
                ]}
              />
            </label>
          )}
          {fieldDef && (
            <label className="rr-contacts__filter">
              <span className="rr-contacts__filter-label">{t('contacts.filters.field.value')}</span>
              {fieldDef.type === 'boolean' ? (
                <Select
                  value={fieldValueRaw}
                  onChange={(e) => setFieldValueRaw(e.currentTarget.value)}
                  data={[
                    { value: '', label: t('contacts.filters.field.valueAny') },
                    { value: 'true', label: t('contacts.filters.field.yes') },
                    { value: 'false', label: t('contacts.filters.field.no') }
                  ]}
                />
              ) : fieldDef.type === 'select' ? (
                <Select
                  value={fieldValueRaw}
                  onChange={(e) => setFieldValueRaw(e.currentTarget.value)}
                  data={[
                    { value: '', label: t('contacts.filters.field.valueAny') },
                    ...(fieldDef.options ?? []).map((o) => ({ value: o, label: o }))
                  ]}
                />
              ) : (
                <TextInput
                  type={fieldDef.type === 'date' ? 'date' : 'search'}
                  inputMode={fieldDef.type === 'number' ? 'decimal' : undefined}
                  placeholder={t('contacts.filters.field.placeholder')}
                  aria-label={t('contacts.filters.field.value')}
                  value={fieldValueRaw}
                  onChange={(e) => setFieldValueRaw(e.currentTarget.value)}
                />
              )}
            </label>
          )}

          <div className="rr-contacts__filter-actions">
            {filtered && (
              <button
                type="button"
                className="rr-contacts__clear"
                onClick={clearFilters}
              >
                {t('contacts.filters.clear')}
              </button>
            )}

            {/* The count is the point of this control: with the filter row
                scrolled out of view on a laptop, nothing else says the list is
                narrowed, and "no results" then reads as "no contacts". */}
            <button
              type="button"
              className={`rr-contacts__filterbtn${activeFilterCount > 0 ? ' is-active' : ''}`}
              aria-pressed={activeFilterCount > 0}
              onClick={() => searchRef.current?.focus()}
            >
              <FilterIcon />
              {t('contacts.filters.title')}
              {activeFilterCount > 0 && (
                <span className="rr-contacts__filterbtn-count">{activeFilterCount}</span>
              )}
            </button>
          </div>
        </div>

        <div className="rr-contacts__summary">
          <Text role="bodySmall" c="var(--rr-color-text-muted)">
            {t('contacts.showingOf', { count: formatCount(rows.length), total: formatCount(total) })}
          </Text>
          <button
            type="button"
            className="rr-contacts__refresh"
            onClick={() => qc.invalidateQueries({ queryKey: ['contacts'] })}
            disabled={isFetching}
          >
            <RefreshIcon />
            {t('action.refresh')}
          </button>
        </div>

        <BulkBar
          selected={selected}
          onClear={() => setSelected([])}
          // The selection is held as ids because it has to survive a refetch;
          // compose wants numbers, and the rows on screen are where the two
          // meet. The selection is cleared on any page or filter change, so
          // every selected id is on this page.
          onMessage={(ids) => messageContacts(
            ids.map((id) => rows.find((r) => r.id === id)?.msisdn).filter((m): m is string => !!m)
          )}
        />

        {!isPending && rows.length === 0 ? (
          <div className="rr-contacts__empty">
            {/* §4 item 4: "No contacts yet: import a CSV" was shown for a search
                that simply had no match. */}
            <EmptyState
              title={filtered ? t('contacts.emptySearch', { query: query || tag || languageName || fieldDef?.label || '' }) : t('contacts.empty')}
              description={filtered ? t('contacts.emptySearch.hint') : t('contacts.empty.hint')}
              action={
                filtered
                  ? <Button size="xs" variant="default" onClick={clearFilters}>{t('contacts.filters.clear')}</Button>
                  : <Button size="xs" onClick={() => setEditing('new')}>{t('contacts.actions.create')}</Button>
              }
            />
          </div>
        ) : narrow ? (
          /* A seven-column table at 375px is two swipes wide. Cards below 820px,
             the pattern admin-ui already uses. */
          <ContactCards
            rows={rows}
            loading={isPending}
            onOpen={openContact}
            onOpenPage={(c) => navigate(`/contacts/${encodeURIComponent(c.id)}`)}
            onEdit={setEditing}
            onDelete={confirmDelete}
            // WhatsApp needs a number; an email-only contact has none.
            onMessage={(c) => { if (c.msisdn) messageContacts([c.msisdn]); }}
          />
        ) : (
          <DataTable<Indexed<contacts.Contact>>
            records={rows}
            loading={isPending || isFetching}
            idAccessor="id"
            onRowClick={openContact}
            emptyState={t('contacts.empty')}
            columns={[
              {
                accessor: 'select',
                title: (
                  <input
                    type="checkbox"
                    aria-label={t('contacts.select.all')}
                    checked={allOnPageSelected}
                    onChange={toggleAll}
                    onClick={(e) => e.stopPropagation()}
                  />
                ),
                render: (c) => (
                  <input
                    type="checkbox"
                    aria-label={t('contacts.select.one', { name: contactLabelOf(c) })}
                    checked={selected.includes(c.id)}
                    onChange={() => toggleOne(c.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )
              },
              {
                accessor: 'name',
                title: sortable('name', t('contacts.columns.contact')),
                render: (c) => <ContactIdentity name={c.name} msisdn={c.msisdn} email={c.email} />
              },
              {
                accessor: 'tags',
                title: t('inbox.tags.title'),
                render: (c) => <ContactTags tags={c.tags ?? []} />
              },
              {
                accessor: 'opt_in',
                title: sortable('opt_in', t('contacts.columns.optIn'), t('contacts.columns.optIn.hint')),
                render: (c) => <OptInPill optedIn={c.opt_in} />
              },
              {
                accessor: 'locale',
                title: t('contacts.columns.locale'),
                render: (c) => c.locale ?? <Muted t={t} />
              },
              {
                accessor: 'updated_at',
                title: sortable('updated_at', t('common.updated')),
                // §4 item 12: a bare ISO date in an app with a timezone-aware
                // formatter the inbox already uses.
                render: (c) => formatDate(c.updated_at)
              },
              {
                accessor: 'actions',
                // §4 item 16: an unnamed column announces as "column 8".
                title: t('common.actions'),
                render: (c) => (
                  <div onClick={(e) => e.stopPropagation()}>
                    {/* §4 item 15: Delete was a text button inside a clickable
                        row, one mis-click from destroying a consent record. */}
                    <Menu>
                      <Menu.Target>
                        <Button size="xs" variant="subtle" aria-label={t('common.actions')}>⋯</Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {/* Open shows the whole contact on its own page; a click on the row opens the drawer. */}
                        <Menu.Item onClick={() => navigate(`/contacts/${encodeURIComponent(c.id)}`)}>{t('action.open')}</Menu.Item>
                        {/* The shortest honest path from a contact to a
                            message. Before compose existed the only way to
                            reach one person from here was to tag them and
                            send a broadcast to the tag. */}
                        {/* WhatsApp needs a number, which an email-only
                            contact does not have. */}
                        {c.msisdn && (
                          <Menu.Item onClick={() => messageContacts([c.msisdn!])}>
                            {t('contacts.actions.message')}
                          </Menu.Item>
                        )}
                        <Menu.Divider />
                        <Menu.Item color="red" onClick={() => confirmDelete(c)}>{t('contacts.remove.action')}</Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </div>
                )
              }
            ]}
          />
        )}

        {/* Rows-per-page beside the pager, as in the reference. The old page
            size was a constant, so a book of 4 000 was walked 50 at a time
            whether the person wanted that or not. */}
        {!narrow && rows.length > 0 && (
          <div className="rr-contacts__pager">
            <label className="rr-contacts__perpage">
              <span>{t('contacts.rowsPerPage')}</span>
              <Select
                size="xs"
                aria-label={t('contacts.rowsPerPage')}
                value={String(pageSize)}
                onChange={(e) => setPageSize(Number(e.currentTarget.value))}
                data={PAGE_SIZES.map((n) => ({ value: String(n), label: String(n) }))}
              />
            </label>

            <Pager page={page} lastPage={lastPage} onPage={setPage} />
          </div>
        )}
      </Card>

      {/* The import card from the reference: what the file needs to contain,
          where the template is, and a way to put it away once it is known. */}
      {importHelpOpen && (
        <Card withBorder className="rr-contacts__importcard">
          <div className="rr-contacts__importcard-icon" aria-hidden><UploadIcon size={22} /></div>
          <div className="rr-contacts__importcard-text">
            <Text role="body" fw={650}>{t('contacts.importCard.title')}</Text>
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('contacts.importCard.body')}</Text>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('contacts.importCard.columns')}</Text>
            {smsAvailable && <Text role="caption" c="var(--rr-color-text-muted)">{t('contacts.importCard.smsColumn')}</Text>}
            <Text role="caption" c="var(--rr-color-text-muted)">{t('contacts.importCard.emailColumn')}</Text>
          </div>
          <div className="rr-contacts__importcard-actions">
            <Button size="xs" variant="default" leftSection={<DownloadIcon />} onClick={downloadTemplate}>
              {t('contacts.importCard.template')}
            </Button>
            <Button
              size="xs"
              variant="subtle"
              aria-label={t('action.dismiss')}
              onClick={() => setImportHelpOpen(false)}
            >
              ✕
            </Button>
          </div>
        </Card>
      )}

      <ContactDrawer
        contact={editing}
        onClose={closeContact}
        onSaved={() => qc.invalidateQueries({ queryKey: ['contacts'] })}
      />
    </Stack>
  );
}

/** A starter file in the exact shape the importer reads. */
function downloadTemplate() {
  // File content, not UI copy: the header row is the contract the importer
  // reads and the two examples show the tag separator. Translating either
  // would produce a template the service cannot parse.
  const csv = [
    'msisdn,name,email,locale,tags',
    '+27821110001,Mary Rodriguez,mary@example.com,en,VIP;Lead', // i18n-ignore: file content
    '+27821110002,Thomas Heinz,thomas@example.com,af,Web',      // i18n-ignore: file content
    ',Lerato Khumalo,lerato@example.com,en,Newsletter'          // i18n-ignore: file content (email only)
  ].join('\n');
  const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'contacts-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Numbered pages with ellipses, as in the reference.
 *
 * sdk-ui has a `Pagination`, and `DataTable` drives it, but the reference puts
 * the pager beside the rows-per-page control OUTSIDE the table, so it is built
 * here from the same page maths.
 */
function Pager({ page, lastPage, onPage }: { page: number; lastPage: number; onPage: (p: number) => void }) {
  const t = useT();
  if (lastPage <= 1) return null;

  /*
   * Seven slots: the first page, the last page, and a run of five around the
   * current one. Near either end the run is anchored rather than centred, so
   * page 1 of 50 offers 1-5 and not just 1-3: a window centred on the current
   * page gives the fewest choices exactly where a reader starts.
   */
  const RUN = 5;
  let start = Math.max(1, page - Math.floor(RUN / 2));
  let end = start + RUN - 1;
  if (end > lastPage) { end = lastPage; start = Math.max(1, end - RUN + 1); }

  const pages: (number | 'gap')[] = [];
  for (let i = 1; i <= lastPage; i++) {
    if (i === 1 || i === lastPage || (i >= start && i <= end)) pages.push(i);
    else if (pages[pages.length - 1] !== 'gap') pages.push('gap');
  }

  return (
    <nav className="rr-contacts__pages" aria-label={t('contacts.pagination')}>
      <button
        type="button"
        className="rr-contacts__page"
        aria-label={t('contacts.pagination.previous')}
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        ‹
      </button>
      {pages.map((p, i) => p === 'gap' ? (
        <span key={`gap-${i}`} className="rr-contacts__page-gap" aria-hidden>…</span>
      ) : (
        <button
          key={p}
          type="button"
          className={`rr-contacts__page${p === page ? ' is-active' : ''}`}
          aria-current={p === page ? 'page' : undefined}
          aria-label={t('contacts.pagination.page', { page: p })}
          onClick={() => onPage(p)}
        >
          {p}
        </button>
      ))}
      <button
        type="button"
        className="rr-contacts__page"
        aria-label={t('contacts.pagination.next')}
        disabled={page >= lastPage}
        onClick={() => onPage(page + 1)}
      >
        ›
      </button>
    </nav>
  );
}

function SearchIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" aria-hidden>
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.4 10.4 L14 14" />
    </svg>
  );
}
function RefreshIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" />
      <polyline points="13.7,1.8 13.7,5 10.5,5" />
    </svg>
  );
}
function DownloadIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 1.8v8.4" />
      <polyline points="4.6,7 8,10.4 11.4,7" />
      <path d="M2.3 13.2h11.4" />
    </svg>
  );
}
function UploadIcon({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 10.4V2" />
      <polyline points="4.6,5.2 8,1.8 11.4,5.2" />
      <path d="M2.3 13.2h11.4" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" aria-hidden>
      <path d="M8 3.2v9.6M3.2 8h9.6" />
    </svg>
  );
}

function Muted({ t }: { t: (k: string) => string }) {
  return <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>;
}

function FilterIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 3.5h12L9.5 8.8V13L6.5 11.5V8.8z" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" aria-hidden>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 7.3v3.6" />
      <circle cx="8" cy="5.2" r="0.75" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Both carets, with the active direction lit: the inactive state has to be
 *  visible or the column does not look sortable at all. */
function SortIcon({ direction }: { direction: 'asc' | 'desc' | null }) {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" aria-hidden className="rr-contacts__sort-icon">
      <path d="M5 1.5 8.2 5.4H1.8z" fill="currentColor" opacity={direction === 'asc' ? 1 : 0.32} />
      <path d="M5 12.5 1.8 8.6h6.4z" fill="currentColor" opacity={direction === 'desc' ? 1 : 0.32} />
    </svg>
  );
}
